import { BriefcaseMedical } from "lucide-react";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import { CardActions, createCardActionsProps } from "@/src/components/cards/card-actions";
import {
  EntityDropdownMenu,
  Field,
  FieldLayout,
  hasConflicts,
  LinkToEntity,
  renderBadge,
} from "@/src/components/cards/common";
import PractitionerCardEditable from "@/src/components/cards/practitioner-card/practitioner-card-editable";
import ErrorAlert from "@/src/components/error-alert";
import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import { Skeleton } from "@/src/components/shadcn/ui/skeleton";
import { ValerieCard } from "@/src/components/valerie-card";
import { useUser } from "@/src/context/user-context";
import { logger } from "@/src/lib/logging/logger";
import { canWriteSOR } from "@/src/lib/rbac";
import { Practitioner } from "@/src/models/practitioner";

const PRACTITIONER_FIELDS: Field[] = [
  { key: "familyName", label: "Family Name", comparable: true, section: "Name" },
  { key: "givenName", label: "Given Name", comparable: true, section: "Name" },
  { key: "middleName", label: "Middle Name", comparable: false, section: "Name" },
  { key: "npiNumber", label: "NPI", comparable: true, section: "Details" },
  { key: "ehrId", label: "EHR ID", comparable: false, section: "Details" },
  { key: "specialty", label: "Specialty", comparable: false, section: "Details" },
];

export default function PractitionerCard({
  practitioner,
  type,
  isEditable = false,
  compareMode = false,
  secondaryPractitioners = null,
  onEditPractitioner,
  onEditPractitionerLoading,
}: {
  practitioner: Practitioner;
  type?: "Referring" | "Receiving" | "Primary Care" | "Ordering";
  isEditable?: boolean;
  compareMode?: boolean;
  secondaryPractitioners?: Practitioner[] | null;
  // Callback for when user switches to a different practitioner - allows parent to handle the context update
  onEditPractitioner?: (updatedPractitioner: Practitioner) => Promise<void>;
  onEditPractitionerLoading?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const pathname = usePathname();
  const { roles } = useUser();
  const canWrite = canWriteSOR(roles);
  const canEdit = isEditable && canWrite;

  // Find the first non-conflicting secondary practitioner from the array
  // If any match, use that one (will show "Synced")
  // If all conflict, use the first one (will show "Conflicts")
  function findBestMatchingSecondaryPractitioner(
    primary: Practitioner,
    secondaries: Practitioner[] | null
  ): Practitioner | null {
    if (!secondaries || secondaries.length === 0) {
      return null;
    }

    const nonConflicting = secondaries.find((secondary) => !hasConflicts(PRACTITIONER_FIELDS, primary, secondary));

    return nonConflicting ?? secondaries[0];
  }

  const displaySecondaryPractitioner = useMemo(
    () => (secondaryPractitioners ? findBestMatchingSecondaryPractitioner(practitioner, secondaryPractitioners) : null),
    [practitioner, secondaryPractitioners]
  );

  useEffect(() => {
    logger.info("Practitioner in practitioner card", {
      practitioner: practitioner,
      secondaryPractitioner: displaySecondaryPractitioner,
      secondaryPractitionersCount: secondaryPractitioners?.length || 0,
    });
  }, [practitioner, displaySecondaryPractitioner, secondaryPractitioners]);

  let hasAnyConflicts = false;

  if (compareMode && displaySecondaryPractitioner) {
    hasAnyConflicts = hasConflicts(PRACTITIONER_FIELDS, practitioner, displaySecondaryPractitioner);
  }

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  if (isEditing) {
    return (
      <PractitionerCardEditable
        practitioner={practitioner}
        onClose={handleEditToggle}
        type={type}
        onEditPractitioner={onEditPractitioner}
        onEditPractitionerLoading={onEditPractitionerLoading}
      />
    );
  }

  const cardActionsProps = createCardActionsProps({
    id: practitioner.id,
    label: "Practitioner ID",
    href: `/providers/${practitioner.displayId}`,
    customProps: canEdit ? [{ label: "Edit Practitioner", onClick: handleEditToggle }] : undefined,
  });

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <ValerieCard
          className="w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          tabIndex={0}
          icon={<BriefcaseMedical />}
          title={type ? `${type} Provider` : "Practitioner"}
          actions={
            <>
              {practitioner && renderBadge(compareMode, displaySecondaryPractitioner, hasAnyConflicts)}
              {practitioner && !pathname?.startsWith(`/providers/${practitioner.displayId}`) ? (
                <LinkToEntity href={`/providers/${practitioner.displayId}`} />
              ) : null}
              {practitioner && canWrite && <EntityDropdownMenu cardActionsProps={cardActionsProps} />}
            </>
          }
        >
          {!practitioner ? (
            <ErrorAlert message="Practitioner not found" compact />
          ) : (
            <FieldLayout
              fields={PRACTITIONER_FIELDS}
              patient={practitioner}
              secondaryObject={displaySecondaryPractitioner}
              compareMode={compareMode}
            />
          )}
        </ValerieCard>
      </ContextMenuTrigger>
      {practitioner && canWrite && <CardActions {...cardActionsProps} isContextMenu={true} />}
    </ContextMenu>
  );
}

function GridItemSkeleton() {
  return <Skeleton className="inline-block align-text-bottom h-4 w-[100px]" />;
}

export function PractitionerCardSkeleton() {
  return (
    <ValerieCard className="w-full" icon={<BriefcaseMedical />} title="Practitioner">
      <div className="grid grid-cols-3 gap-4">
        {Array.from({ length: 6 }, (_, index) => (
          <GridItemSkeleton key={index} />
        ))}
      </div>
    </ValerieCard>
  );
}
